import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { accessCodeApi, organizationApi, AccessCode, Organization } from '../../services/api';
import { CodeDisplayModal } from '../../components/CodeDisplayModal';

export function AccessCodesList(): JSX.Element {
  const { user } = useAuth();
  const [accessCodes, setAccessCodes] = useState<AccessCode[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);

  // Form state
  const [formOrgId, setFormOrgId] = useState<number | ''>('');
  const [formDescription, setFormDescription] = useState('');
  const [formMaxUses, setFormMaxUses] = useState<number | ''>('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter state
  const [filterType, setFilterType] = useState<string>('');
  const [filterOrgId, setFilterOrgId] = useState<number | ''>('');

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [codesRes, orgsRes] = await Promise.all([
        accessCodeApi.getAll({
          type: filterType || undefined,
          organizationId: filterOrgId || undefined,
        }),
        organizationApi.getAll(),
      ]);
      setAccessCodes(codesRes.accessCodes || []);
      setOrganizations(orgsRes.organizations || []);
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filterType, filterOrgId]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formOrgId) {
      setFormError('Bitte wählen Sie eine Organisation');
      return;
    }

    setFormError('');
    setIsSubmitting(true);

    try {
      const response = await accessCodeApi.create({
        organizationId: formOrgId as number,
        description: formDescription || undefined,
        maxUses: formMaxUses || undefined,
      });

      setGeneratedCode(response.generatedCode);
      setShowCreateForm(false);
      setFormOrgId('');
      setFormDescription('');
      setFormMaxUses('');
      loadData();
    } catch (err: any) {
      setFormError(err.message || 'Fehler beim Erstellen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegenerate = async (code: AccessCode) => {
    if (!window.confirm('Neuen Code generieren? Der alte Code wird ungültig.')) return;

    try {
      const response = await accessCodeApi.regenerate(code.id);
      setGeneratedCode(response.generatedCode);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Regenerieren');
    }
  };

  const handleToggleActive = async (code: AccessCode) => {
    try {
      await accessCodeApi.update(code.id, { isActive: !code.isActive });
      loadData();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Aktualisieren');
    }
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Laden...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Zugangscodes</h1>
        {user?.isSuperAdmin && !showCreateForm && (
          <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
            Neuer Zugangscode
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {generatedCode && (
        <CodeDisplayModal
          code={generatedCode}
          onClose={() => setGeneratedCode(null)}
        />
      )}

      {showCreateForm && (
        <div className="form-card">
          <h2>Neuer Zugangscode</h2>
          <form onSubmit={handleCreate}>
            {formError && <div className="form-error">{formError}</div>}

            <div className="form-group">
              <label htmlFor="orgId">Organisation</label>
              <select
                id="orgId"
                value={formOrgId}
                onChange={e => setFormOrgId(e.target.value ? Number(e.target.value) : '')}
                required
              >
                <option value="">-- Bitte wählen --</option>
                {organizations.filter(o => o.isActive).map(org => (
                  <option key={org.id} value={org.id}>
                    {org.name} ({org.storeId})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="description">Beschreibung (optional)</label>
              <input
                type="text"
                id="description"
                value={formDescription}
                onChange={e => setFormDescription(e.target.value)}
                placeholder="z.B. Mitarbeiter Schulung Januar"
              />
            </div>

            <div className="form-group">
              <label htmlFor="maxUses">Max. Verwendungen (optional)</label>
              <input
                type="number"
                id="maxUses"
                value={formMaxUses}
                onChange={e => setFormMaxUses(e.target.value ? Number(e.target.value) : '')}
                min="1"
                placeholder="Unbegrenzt"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Erstellen...' : 'Erstellen'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreateForm(false)}>
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="filters">
        <div className="filter-group">
          <label>Typ:</label>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">Alle</option>
            <option value="USER">Benutzer</option>
            <option value="ADMIN">Admin</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Organisation:</label>
          <select value={filterOrgId} onChange={e => setFilterOrgId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Alle</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Typ</th>
              <th>Beschreibung</th>
              <th>Organisation</th>
              <th>Nutzung</th>
              <th>Status</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {accessCodes.length === 0 ? (
              <tr>
                <td colSpan={6} className="no-data">Keine Zugangscodes vorhanden</td>
              </tr>
            ) : (
              accessCodes.map(code => (
                <tr key={code.id} className={!code.isActive ? 'inactive' : ''}>
                  <td>
                    <span className={`type-badge ${code.type.toLowerCase()}`}>
                      {code.type === 'ADMIN' ? 'Admin' : 'Benutzer'}
                    </span>
                  </td>
                  <td>{code.description || '-'}</td>
                  <td>{code.organization?.name || '-'}</td>
                  <td>
                    {code.usageCount}
                    {code.maxUses && ` / ${code.maxUses}`}
                  </td>
                  <td>
                    <span className={`status-badge ${code.isActive ? 'active' : 'inactive'}`}>
                      {code.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="actions">
                    {code.type !== 'ADMIN' && (
                      <>
                        <button className="btn btn-small" onClick={() => handleRegenerate(code)}>
                          Neu generieren
                        </button>
                        <button
                          className={`btn btn-small ${code.isActive ? 'btn-danger' : 'btn-success'}`}
                          onClick={() => handleToggleActive(code)}
                        >
                          {code.isActive ? 'Deaktivieren' : 'Aktivieren'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AccessCodesList;
